import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Typography, Skeleton } from "@mui/material";

import { Service } from "../../../types/service.type";
import { EquipmentCategory } from "../../../enum/service.enum";
import { getServiceEquipments } from "../../../utils/serviceEquipmentsUtils";

export const ProgramEquipments = ({ service, isServiceLoading }: { service?: Service; isServiceLoading?: boolean }) => {
  const [currentCategory, setCurrentCategory] = useState<EquipmentCategory>(EquipmentCategory.ball_control_and_agility);
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });
  const { t: tEquipments } = useTranslation("goService", { keyPrefix: "equipments" });

  if (isServiceLoading) return <ProgramEquipmentsLoader />;

  const { programContents } = service!;

  const equipments = getServiceEquipments(programContents);
  const equipmentCategories = Object.values(EquipmentCategory);

  const handleCategoryClick = (category: EquipmentCategory) => setCurrentCategory(category);

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.title}>{t("equipmentsNeeded")}</Typography>

      <Box sx={styles.container}>
        <Box sx={styles.categoriesWrapper}>
          {equipmentCategories.map((cat) => (
            <Chip
              key={cat}
              label={tEquipments(cat)}
              sx={styles.categoryChip(currentCategory === cat)}
              onClick={() => handleCategoryClick(cat)}
            />
          ))}
        </Box>
      </Box>
      <Box sx={styles.equipmentsWrapper}>
        {equipments?.[currentCategory].length === 0 && (
          <Typography sx={styles.noEquipments}>{t("noEquipments")}</Typography>
        )}
        {equipments?.[currentCategory]?.map((equipment) => (
          <Chip key={equipment} label={equipment} sx={styles.equipmentChip} />
        ))}
      </Box>
    </Box>
  );
};

export const ProgramEquipmentsLoader = () => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.title}>{t("equipmentsNeeded")}</Typography>
      <Box sx={styles.container}>
        <Box sx={styles.categoriesWrapper}>
          {[...Array(2)].map((_, index) => (
            <Skeleton key={index} width={100} height={32} sx={styles.categorySkeleton} />
          ))}
        </Box>
      </Box>
      <Box sx={styles.equipmentsWrapper}>
        {[...Array(4)].map((_, index) => (
          <Skeleton key={index} width={80} height={32} sx={styles.equipmentSkeleton} />
        ))}
      </Box>
    </Box>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  container: {
    width: "100%",
    overflowX: "auto",
    whiteSpace: "nowrap",
    padding: "0 0 8px",
  },
  categoriesWrapper: {
    display: "flex",
    gap: "10px",
    width: "fit-content",
  },
  categoryChip: (isActive: boolean) => ({
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    padding: "8px 12px",
    background: isActive ? "#F5F6F5" : "transparent",
    "& .MuiChip-label": {
      padding: "0",
    },
  }),
  equipmentChip: {
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    padding: "4px 12px",
    "& .MuiChip-label": {
      padding: "0",
    },
  },
  equipmentsWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  noEquipments: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  categorySkeleton: {
    borderRadius: "16px",
  },
  equipmentSkeleton: {
    borderRadius: "16px",
  },
} as const;
